import {
  BriefcaseBusiness,
  HandshakeIcon,
  MailIcon,
  ShieldCheck,
  Users as UsersIcon,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { pages } from '@/modules/layouts/pages';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/modules/ui/components/sidebar/Sidebar';

const iconSize = 20;

const menuItems = [
  {
    icon: <BriefcaseBusiness size={iconSize} />,
    label: 'Jobs',
    path: '/jobs',
  },
  {
    icon: <MailIcon size={iconSize} />,
    label: 'Messaging',
    path: '/messaging',
  },
  {
    icon: <HandshakeIcon size={iconSize} />,
    label: 'Companies',
    path: '/companies',
  },
  {
    icon: <UsersIcon size={iconSize} />,
    label: 'Users',
    path: '/users',
  },
  {
    icon: <ShieldCheck size={iconSize} />,
    label: 'User Admin',
    path: '/administration/users',
  },
] satisfies {
  icon: React.ReactNode;
  label: string;
  path: string;
}[];

const AppSidebar = () => {
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  const location = useLocation();

  const activeItem =
    location.pathname === '/'
      ? '/jobs'
      : menuItems.find(
          (item) =>
            location.pathname === item.path ||
            location.pathname.startsWith(item.path + '/'),
        )?.path;

  return (
    <Sidebar
      variant="sidebar"
      collapsible="icon"
      className="finlay-sidebar border-border/40 border-r shadow-sm"
      style={
        {
          '--sidebar-width-icon': 'calc(var(--spacing) * 23)',
          '--header-height': 'calc(var(--spacing) * 12)',
        } as React.CSSProperties
      }
    >
      <SidebarHeader className="border-border/40 border-b px-8 pt-4 pb-0">
        {isCollapsed ? (
          <div className="pt-8 pb-4">
            <img
              src="/blob.png"
              alt="Finlay Logo"
              className="h-8 w-auto object-contain"
            />
          </div>
        ) : (
          <div className="p-4 pt-8 pl-0">
            <img
              src="/logo.png"
              alt="Finlay Logo"
              className="h-7 w-auto object-contain"
            />
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="px-2 py-4">
        <SidebarMenu>
          {menuItems.map((item) => {
            const pageConfig = pages.find((page) => page.path === item.path);

            return (
              <PermissionsGate
                key={item.path}
                permissions={pageConfig?.permissions ?? []}
              >
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={activeItem === item.path}
                    tooltip={item.label}
                  >
                    <Link to={item.path} className="flex items-center gap-3">
                      <span className="text-xl">{item.icon}</span>
                      <span className="text-md">{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </PermissionsGate>
            );
          })}
        </SidebarMenu>
      </SidebarContent>

      <SidebarFooter className="p-4">
        <SidebarTrigger
          className="w-full justify-center"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        />
      </SidebarFooter>
    </Sidebar>
  );
};
export default AppSidebar;
